'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@shared/components/shadcn';
import type { ContactPreferenceChannel } from '@shared/config';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { FieldMessage } from '../../../form-controls/FieldMessage/FieldMessage';
import { ToggleGroup, type ToggleOption } from '../../../form-controls/ToggleGroup/ToggleGroup';
import { TargetedAdvertisingToggle } from './TargetedAdvertisingToggle';

const contactPreferenceChannels = ['email', 'sms', 'phone', 'post', 'trustedPartners'] as const satisfies readonly ContactPreferenceChannel[];

type ChannelsFormProps = {
  initialSelected: ContactPreferenceChannel[];
  selected?: ContactPreferenceChannel[];
  onSelectedChange?: (selected: ContactPreferenceChannel[]) => void;
  options: ToggleOption[];
  onSubmit: (selected: ContactPreferenceChannel[]) => Promise<void>;
};

type FormData = {
  selected: ContactPreferenceChannel[];
};

function arraysEqualAsSets<T>(firstArray: readonly T[], secondArray: readonly T[]): boolean {
  if (firstArray.length !== secondArray.length) return false;
  const firstSet = new Set(firstArray);
  const secondSet = new Set(secondArray);
  if (firstSet.size !== secondSet.size) return false;
  for (const value of firstSet) {
    if (!secondSet.has(value)) return false;
  }
  return true;
}

function toTrackYesNo(selected: readonly ContactPreferenceChannel[], channel: ContactPreferenceChannel): 'Yes' | 'No' {
  return selected.includes(channel) ? 'Yes' : 'No';
}

function ChannelsForm({ initialSelected, selected, onSelectedChange, options, onSubmit }: ChannelsFormProps) {
  const t = useTranslations('my_account');

  const schema = useMemo(
    () =>
      z.object({
        selected: z.array(z.enum(contactPreferenceChannels)),
      }),
    [t]
  );

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { selected: initialSelected },
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  const { setValue } = form;
  const isControlled = selected !== undefined;

  useEffect(() => {
    if (isControlled) return;
    setValue('selected', initialSelected);
  }, [initialSelected, isControlled, setValue]);

  const currentSelected: ContactPreferenceChannel[] = isControlled ? selected : (form.watch('selected') ?? []);
  const hasChanges = !arraysEqualAsSets(currentSelected, initialSelected);

  const updateSelected = (next: ContactPreferenceChannel[]) => {
    form.setValue('selected', next);
    onSelectedChange?.(next);
  };

  const handleSubmit = form.handleSubmit(async values => {
    const submitted = selected ?? values.selected ?? [];
    await onSubmit(submitted);
  });

  const hasError = !!form.formState.errors.selected?.message;

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-6">
        <ToggleGroup
          value={currentSelected}
          onValueChange={values => updateSelected(values as ContactPreferenceChannel[])}
          options={options}
          hasError={hasError}
          data-track-container="Options"
        />
        <FieldMessage id="channels-error" message={form.formState.errors.selected?.message} messageType={hasError ? 'error' : undefined} data-track-helper="Channel Preferences" />
        <TargetedAdvertisingToggle
          checked={currentSelected.includes('trustedPartners')}
          onCheckedChange={checked => {
            const next: ContactPreferenceChannel[] = checked ? [...currentSelected, 'trustedPartners'] : currentSelected.filter(value => value !== 'trustedPartners');
            updateSelected(next);
          }}
          hasError={hasError}
        />
        <div className="mt-auto flex" data-track-container="Actions">
          <Button
            variant="primary"
            className="w-full"
            onClick={handleSubmit}
            disabled={!hasChanges}
            data-testid="channels-save-button"
            data-track-name="Continue"
            data-track-communication-email={toTrackYesNo(currentSelected, 'email')}
            data-track-communication-sms={toTrackYesNo(currentSelected, 'sms')}
            data-track-communication-phone={toTrackYesNo(currentSelected, 'phone')}
            data-track-communication-post={toTrackYesNo(currentSelected, 'post')}
          >
            {t('settings.contact_preferences.channels_save')}
          </Button>
        </div>
      </div>
    </FormProvider>
  );
}

export { ChannelsForm };
